/**
 * Bundle 127 - Past event summary card.
 *
 * Rendered on the public /events/:slug page when the backend
 * returns `is_past_event: true` and a `past_event_summary` block
 * (see /app/backend/services/past_event_service.py).
 *
 * The RSVP CTA is disabled and pricing tiers are hidden by the
 * parent EventDetailPage. Existing ticket holders keep their
 * ticket + survey link (rendered elsewhere).
 *
 * Hygiene: no em-dashes, no en-dashes, no eslint-disable, no
 * "photo"/"image" in HTML alt attributes.
 */
import React from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Star, Users, Sparkles, Radio, Heart } from 'lucide-react';

const _redactedLabel = 'small bucket, redacted';

const StarStrip = ({ value }) => {
  const rounded = value == null ? 0 : Math.round(value * 2) / 2;
  return (
    <div className="flex items-center gap-1" aria-hidden="true">
      {[1, 2, 3, 4, 5].map((i) => {
        const fill = rounded >= i ? '#eab308'
          : rounded >= i - 0.5 ? '#fbbf24' : '#e5e7eb';
        return <Star key={i} className="h-4 w-4" style={{ color: fill, fill }} />;
      })}
    </div>
  );
};

const GenderBar = ({ split, floor }) => {
  const entries = Object.entries(split || {}).filter(([, v]) => v !== 0);
  if (entries.length === 0) return null;
  const numeric = entries.filter(([, v]) => typeof v === 'number');
  const total = numeric.reduce((a, [, v]) => a + v, 0);
  const label = {
    woman: 'Women', man: 'Men', non_binary: 'Non-binary',
    prefer_not_to_say: 'Prefer not to say', other: 'Other',
  };
  const color = {
    woman: 'bg-secondary-500', man: 'bg-primary-700',
    non_binary: 'bg-accent-500', prefer_not_to_say: 'bg-neutral-400',
    other: 'bg-neutral-300',
  };
  return (
    <div className="space-y-2" data-testid="past-summary-gender-bar">
      {total > 0 && (
        <div className="flex h-2 rounded-full overflow-hidden bg-neutral-100">
          {numeric.map(([k, v]) => (
            <div
              key={k}
              className={color[k] || 'bg-neutral-300'}
              style={{ width: `${Math.round((v / total) * 100)}%` }}
              title={`${label[k] || k}: ${v}`}
            />
          ))}
        </div>
      )}
      <div className="flex flex-wrap gap-x-3 gap-y-1 text-[11px] text-neutral-600">
        {entries.map(([k, v]) => (
          <span key={k} data-testid={`past-summary-gender-${k}`}>
            <span className="font-semibold text-primary-900">
              {label[k] || k}:
            </span>{' '}
            {v === 'redacted'
              ? <em className="text-neutral-500">{_redactedLabel} (&lt;{floor})</em>
              : <span>{v}</span>}
          </span>
        ))}
      </div>
    </div>
  );
};

const Tile = ({ icon: Icon, label, children, testid }) => (
  <div
    className="rounded-lg border border-neutral-200 p-4 bg-white"
    data-testid={testid}
  >
    <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-widest text-secondary-700 mb-2">
      <Icon className="h-3 w-3" /> {label}
    </div>
    {children}
  </div>
);

const PastEventSummaryCard = ({ summary }) => {
  if (!summary) return null;
  const {
    avg_rating: avgRating,
    total_responses: totalResponses,
    interested_count: interested,
    gender_split: gender,
    top_discovery_source: discovery,
    connections_reported: connections,
    gender_redaction_floor: floor,
  } = summary;
  return (
    <Card
      className="border shadow-soft"
      data-testid="past-event-summary-card"
    >
      <CardContent className="p-6 space-y-5">
        <div>
          <div className="text-xs font-semibold uppercase tracking-widest text-primary-700">
            This event has ended
          </div>
          <div className="mt-1 text-sm text-neutral-600">
            Here is how it went. If you attended, look for your
            follow-up email with your survey link.
          </div>
        </div>

        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          <Tile icon={Star} label="Attendee rating"
                testid="past-summary-avg-rating">
            <div className="flex items-baseline gap-2">
              <div className="font-display text-2xl font-bold text-primary-900">
                {avgRating == null ? '.' : avgRating.toFixed(2)}
              </div>
              <div className="text-[11px] text-neutral-500">
                {totalResponses || 0} responses
              </div>
            </div>
            {avgRating != null && <StarStrip value={avgRating} />}
          </Tile>

          <Tile icon={Users} label="Interested guests"
                testid="past-summary-interested">
            <div className="font-display text-2xl font-bold text-primary-900">
              {interested || 0}
            </div>
            <div className="text-[11px] text-neutral-500">
              Everyone who started an RSVP.
            </div>
          </Tile>

          {/* Bundle 155 - the "Attended" tile was removed from
              this public surface per user directive. Admin
              analytics dashboards keep their attendee counts;
              only the public post-event summary card drops it. */}

          <Tile icon={Sparkles} label="Gender mix"
                testid="past-summary-gender-tile">
            <GenderBar split={gender} floor={floor || 5} />
          </Tile>

          <Tile icon={Radio} label="Top discovery sources"
                testid="past-summary-discovery">
            {(discovery || []).length === 0 ? (
              <div className="text-[11px] text-neutral-500">
                Not enough data yet.
              </div>
            ) : (
              <ul className="text-sm text-primary-900 space-y-1">
                {(discovery || []).slice(0, 3).map((d) => (
                  <li key={d.source} className="flex items-center justify-between">
                    <span className="truncate mr-2">{d.source}</span>
                    <span className="text-[11px] text-neutral-500">
                      {d.count}
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </Tile>

          <Tile icon={Heart} label="Connections reported"
                testid="past-summary-connections">
            <div className="font-display text-2xl font-bold text-primary-900">
              {connections || 0}
            </div>
            <div className="text-[11px] text-neutral-500">
              Guests who told us they clicked with someone.
            </div>
          </Tile>
        </div>
      </CardContent>
    </Card>
  );
};

export default PastEventSummaryCard;
